import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';
import { useOptionalI18n } from '@/i18n';
import { localizeStringList, localizeTemplate, localizeText } from '@/i18n/options';

interface SearchableInputProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  strictSelection?: boolean;
  confirmCustomInput?: boolean;
  error?: string;
}

const SearchableInput: React.FC<SearchableInputProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search or type...',
  disabled = false,
  className = '',
  strictSelection = false,
  confirmCustomInput = false,
  error,
}) => {
  const { locale } = useOptionalI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [lastValidValue, setLastValidValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const referenceRef = useRef<HTMLElement | null>(null);
  const hasInitialized = useRef(false);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            '--dropdown-max-height': `${Math.min(availableHeight - 8, 240)}px`,
          } as React.CSSProperties);
        },
      }),
    ],
  });

  useEffect(() => {
    setSearchTerm(value);
    const isValidOption = options.some((opt) => opt.toLowerCase() === value.toLowerCase());
    if (isValidOption || value === '' || !hasInitialized.current) {
      setLastValidValue(value);
      hasInitialized.current = true;
    }
  }, [value, options]);

  const localizedOptions = localizeStringList(locale, options);
  const filteredOptions = options.filter((opt) => opt.toLowerCase().includes(searchTerm.toLowerCase()));

  const setReferenceNode = (node: HTMLElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;

      if (referenceNode?.contains(target)) return;
      if (floatingNode?.contains(target)) return;

      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
        if (strictSelection && searchTerm) {
          const isValidOption = options.some((opt) => opt.toLowerCase() === searchTerm.toLowerCase());
          if (!isValidOption) {
            setSearchTerm(lastValidValue);
            onChange(lastValidValue);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [strictSelection, searchTerm, options, lastValidValue, onChange, refs.floating]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (confirmCustomInput) {
      if (!isOpen) setIsOpen(true);
      return;
    }

    if (!strictSelection) {
      onChange(newValue);
    } else {
      const matchingOption = options.find((opt) => opt.toLowerCase() === newValue.toLowerCase());
      if (matchingOption) {
        onChange(matchingOption);
        setLastValidValue(matchingOption);
      }
    }

    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (option: string) => {
    setSearchTerm(option);
    onChange(option);
    setLastValidValue(option);
    if (confirmCustomInput) setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSearchTerm('');
    onChange('');
    setLastValidValue('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <div className="relative">
        <input
          ref={(node) => {
            inputRef.current = node;
            setReferenceNode(node);
          }}
          type="text"
          placeholder={localizeText(locale, placeholder)}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          disabled={disabled}
          className={`input-field pr-10 ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'focus:border-primary focus:ring-2 focus:ring-primary'
          } ${disabled ? 'cursor-not-allowed bg-gray-100 opacity-50' : ''}`}
        />
        <div className="absolute right-3 top-1/2 flex -translate-y-1/2 transform items-center gap-1">
          {searchTerm && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded p-1 transition-colors hover:bg-gray-200"
              title={localizeText(locale, 'Clear input')}
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-[1040] rounded-lg border border-primary/20 bg-white shadow-lg"
          >
            <ul className="overflow-y-auto" style={{ maxHeight: 'var(--dropdown-max-height, 240px)' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <li key={`${option}-${index}`}>
                    <button
                      type="button"
                      onClick={() => handleSelect(option)}
                      className={`flex w-full items-center justify-between px-4 py-2 text-left transition-colors ${
                        searchTerm === option ? 'bg-primary/10 text-primary hover:bg-primary/10' : 'hover:bg-primary/5'
                      }`}
                    >
                      <span>{localizedOptions[options.indexOf(option)] ?? option}</span>
                      {searchTerm === option && <span className="text-primary">✓</span>}
                    </button>
                  </li>
                ))
              ) : searchTerm ? (
                <li className="px-4 py-2 text-sm text-gray-500">
                  <div className="mb-2">{localizeText(locale, 'No matching results found')}</div>
                  {!strictSelection && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirmCustomInput) {
                          onChange(searchTerm);
                          setSearchTerm('');
                        }
                        setIsOpen(false);
                      }}
                      className="text-sm font-medium text-primary hover:text-primary"
                    >
                      {localizeTemplate(locale, 'Use "{value}" as custom input', searchTerm)}
                    </button>
                  )}
                </li>
              ) : (
                <li className="px-4 py-2 text-sm text-gray-500">
                  {strictSelection
                    ? localizeText(locale, 'Type to search')
                    : localizeText(locale, 'Type to search or enter custom value')}
                </li>
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default SearchableInput;
